import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import Icon from "@/components/site/Icon";
import { Reveal } from "@/components/site/Reveal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import LeadForm from "@/components/site/LeadForm";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data: s, isLoading, isError } = useQuery({
    queryKey: ["service", slug], queryFn: async () => (await api.get(`/services/${slug}`)).data, retry: false,
  });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  useSeo({ title: s?.title, description: s?.short_desc });

  if (isLoading) return <div className="bs-container py-24 text-center text-muted-foreground">Loading...</div>;
  if (isError || !s) return (
    <div className="bs-container py-24 text-center">
      <p className="text-muted-foreground">Service not found.</p>
      <Button asChild className="mt-4 rounded-full"><Link to="/services">Back to services</Link></Button>
    </div>
  );

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border">
        <div className="bs-aurora pointer-events-none absolute inset-0 opacity-60" />
        <div className="bs-container relative grid items-center gap-10 py-16 lg:grid-cols-2 lg:py-20">
          <div>
            <Link to="/services" className="mb-6 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="mr-1.5 h-4 w-4" /> All services</Link>
            <div className="mb-4 inline-flex rounded-xl bg-primary/12 p-3 text-primary"><Icon name={s.icon} className="h-7 w-7" /></div>
            <Badge variant="secondary" className="ml-3 align-middle">{s.category}</Badge>
            <h1 className="mt-4 font-display text-4xl font-black sm:text-5xl">{s.title}</h1>
            <p className="mt-4 max-w-xl text-muted-foreground">{s.description}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="rounded-full shadow-glow"><Link to="/contact">Request a Quote <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              <span className="text-sm font-semibold text-muted-foreground">{s.starting_price ? `Starting from $${s.starting_price.toLocaleString()}` : "Custom pricing"}</span>
            </div>
          </div>
          <Reveal delay={0.1}>
            <img src={s.image} alt={s.title} className="w-full rounded-3xl border border-border object-cover shadow-soft" />
          </Reveal>
        </div>
      </section>

      {(s.deliverables?.length > 0 || s.process?.length > 0 || s.timeline) && (
        <section className="border-b border-border bg-card/30">
          <div className="bs-container grid gap-8 py-12 md:grid-cols-3">
            {s.deliverables?.length > 0 && (
              <div><h3 className="font-display text-lg font-bold">Deliverables</h3>
                <ul className="mt-3 space-y-2">{s.deliverables.map((d) => <li key={d} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {d}</li>)}</ul></div>
            )}
            {s.process?.length > 0 && (
              <div><h3 className="font-display text-lg font-bold">Process</h3>
                <ol className="mt-3 space-y-2">{s.process.map((d, i) => <li key={d} className="flex items-start gap-2 text-sm"><span className="font-mono text-primary">{i + 1}.</span> {d}</li>)}</ol></div>
            )}
            <div><h3 className="font-display text-lg font-bold">Timeline & budget</h3>
              {s.timeline && <p className="mt-3 text-sm text-muted-foreground"><strong className="text-foreground">Timeline:</strong> {s.timeline}</p>}
              {s.budget_note && <p className="mt-2 text-sm text-muted-foreground">{s.budget_note}</p>}
              {s.category === "Advertising" && <Button asChild className="mt-4 rounded-full"><Link to="/advertising">Build a quote <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>}
            </div>
          </div>
        </section>
      )}

      <section className="bs-container grid gap-12 py-16 lg:grid-cols-[1.2fr_0.8fr] lg:py-20">
        <div>
          <h2 className="font-display text-2xl font-bold">What's included</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {(s.features || []).map((f) => (
              <div key={f} className="flex items-start gap-3 bs-card p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> <span className="text-sm">{f}</span>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <h3 className="font-display text-lg font-bold">Related services</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {services.filter((x) => x.slug !== s.slug).slice(0, 6).map((x) => (
                <Link key={x.slug} to={`/services/${x.slug}`} className="rounded-full border border-border bg-card px-4 py-2 text-sm hover:border-primary/50">{x.title}</Link>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display text-lg font-bold">Request this service</h3>
          <p className="mt-1 mb-4 text-sm text-muted-foreground">Tell us your goals and we'll send a tailored quote.</p>
          <LeadForm type="quote" services={services} showService={false} submitLabel="Request a Quote" />
        </div>
      </section>
    </div>
  );
}
